import { useUser } from '@/@diffrag/providers/user.provider'
import Inquiry from '@/@linkpie/inquiry'
import InquiryEdit from '@/@linkpie/inquiry/edit'
import DoctorInterview from '@/@linkpie/pages/approval-management/doctor-interview'
import DoctorInterviewEdit from '@/@linkpie/pages/approval-management/doctor-interview/edit'
import NewEquipment from '@/@linkpie/pages/approval-management/new-equipment'
import NewEquipmentEdit from '@/@linkpie/pages/approval-management/new-equipment/edit'
import Banner from '@/@linkpie/pages/banner'
import BannerEdit from '@/@linkpie/pages/banner/edit'
import Categories from '@/@linkpie/pages/categories'
import Clinic from '@/@linkpie/pages/clinics'
import ClinicEdit from '@/@linkpie/pages/clinics/edit'
import Comment from '@/@linkpie/pages/comment'
import CommentDetail from '@/@linkpie/pages/comment/detail'
import Community from '@/@linkpie/pages/community'
import Point from '@/@linkpie/pages/point'
import PointTransaction from '@/@linkpie/pages/point-transaction'
import PointTransactionEdit from '@/@linkpie/pages/point-transaction/edit'
import PointEdit from '@/@linkpie/pages/point/edit'
import Products from '@/@linkpie/pages/products'
import ProductEdit from '@/@linkpie/pages/products/edit'
import Promotion from '@/@linkpie/pages/promotion'
import PromotionEdit from '@/@linkpie/pages/promotion/edit'
import Qna from '@/@linkpie/pages/qna'
import Regions from '@/@linkpie/pages/regions'
import RegionEdit from '@/@linkpie/pages/regions/edit'
import Report from '@/@linkpie/pages/report'
import ReportDetail from '@/@linkpie/pages/report/detail'
import User from '@/@linkpie/pages/users'
import UserAdmin from '@/@linkpie/pages/users/admin'
import UserAdminEdit from '@/@linkpie/pages/users/admin/edit'
import UserEdit from '@/@linkpie/pages/users/edit'
import { Error404 } from '@/router/error/Error404'
import Root from '@/router/layout/Root'
import React from 'react'
import {
  Navigate,
  Outlet,
  RouteObject,
  createBrowserRouter,
} from 'react-router-dom'
import { create } from 'zustand'
import { OAuth } from './auth/OAuth'
import { Signin } from './auth/Signin'
import QnaDetail from '@/@linkpie/pages/qna/detail'
import Experience from '@/@linkpie/pages/experience'
import ExperienceEdit from '@/@linkpie/pages/experience/edit'
import CommunityEdit from '@/@linkpie/pages/community/edit'
import FaqDetail from '@/@linkpie/pages/faq/detail'
import FaqEdit from '@/@linkpie/pages/faq/edit'
import Faq from '@/@linkpie/pages/faq'
import Notice from '@/@linkpie/pages/notice'
import NoticeEdit from '@/@linkpie/pages/notice/edit'
import Translation from '@/@linkpie/pages/translation'
import FooterIcon from '@/@linkpie/pages/footer'
import FooterIconEdit from '@/@linkpie/pages/footer/edit'
import ReferralPartners from '@/@linkpie/pages/referral-partners'
import ReferralPartnerEdit from '@/@linkpie/pages/referral-partners/edit'
import ConsultationRequests from '@/@linkpie/pages/consultation-requests'
import ConsultationRequestEdit from '@/@linkpie/pages/consultation-requests/edit'
import ReferralStats from '@/@linkpie/pages/referral-stats'
import { SuperAdminRoute } from '@/router/SuperAdminRoute'

type ProtectedProps = {
  children: React.ReactNode
}

type ProtectedRouteObject = RouteObject & {
  protect?: boolean
  blockUser?: boolean
}

const Protected = ({ children }: ProtectedProps) => {
  const user = useUser()

  if (!user) {
    return <Navigate to="/login" />
  }

  return children
}

const BlockUser = ({ children }: ProtectedProps) => {
  const user = useUser()

  if (user) {
    return <Navigate to="/" />
  }

  return children
}

const routes: ProtectedRouteObject[] = [
  {
    path: '/',
    element: <Root />,
    protect: true,
    children: [
      {
        index: true,
        element: <Navigate to="/main" replace />,
      },
      {
        path: '/main',
        element: <Outlet />,
        children: [],
      },
      // { path: '/settings', element: <Setting /> },
      // { path: '/auth/password', element: <ChangePassword /> },
      // { path: '/design/theme', element: <DesignTheme /> },
      // { path: '/design/theme/edit', element: <DesignThemeEdit /> },
      // { path: '/design/fonts', element: <DesignFont /> },
      // { path: '/design/fonts/edit', element: <DesignFontEdit /> },
      // { path: '/coupon', element: <Coupon /> },
      // { path: '/coupon/edit', element: <CouponEdit /> },
      { path: '/admin', element: <UserAdmin /> },
      { path: '/admin/edit', element: <UserAdminEdit /> },
      // { path: '/users/seller', element: <UserSeller /> },
      // { path: '/users/seller/edit', element: <UserSellerEdit /> },
      { path: '/users', element: <User /> },
      { path: '/users/edit', element: <UserEdit /> },
      { path: '/inquiry', element: <Inquiry /> },
      { path: '/inquiry/edit', element: <InquiryEdit /> },
      { path: '/clinics', element: <Clinic /> },
      { path: '/clinics/edit', element: <ClinicEdit /> },
      {
        path: '/approval-management/doctor-interview',
        element: <DoctorInterview />,
      },
      {
        path: '/approval-management/doctor-interview/edit',
        element: <DoctorInterviewEdit />,
      },
      {
        path: '/approval-management/new-equipment',
        element: <NewEquipment />,
      },
      {
        path: '/approval-management/new-equipment/edit',
        element: <NewEquipmentEdit />,
      },
      { path: '/banner', element: <Banner /> },
      { path: '/banner/edit', element: <BannerEdit /> },
      { path: '/categories', element: <Categories /> },
      { path: '/regions', element: <Regions /> },
      { path: '/regions/edit', element: <RegionEdit /> },
      { path: '/notice', element: <Notice /> },
      { path: '/notice/edit', element: <NoticeEdit /> },
      { path: '/qna', element: <Qna /> },
      { path: '/qna/detail', element: <QnaDetail /> },
      { path: '/faq', element: <Faq /> },
      { path: '/faq/detail', element: <FaqDetail /> },
      { path: '/faq/edit', element: <FaqEdit /> },
      { path: '/promotion', element: <Promotion /> },
      { path: '/promotion/edit', element: <PromotionEdit /> },
      { path: '/experience', element: <Experience /> },
      { path: '/experience/edit', element: <ExperienceEdit /> },
      { path: '/point', element: <Point /> },
      { path: '/point/edit', element: <PointEdit /> },
      { path: '/point-transaction', element: <PointTransaction /> },
      { path: '/point-transaction/edit', element: <PointTransactionEdit /> },
      { path: '/products', element: <Products /> },
      { path: '/products/edit', element: <ProductEdit /> },
      { path: '/community', element: <Community /> },
      { path: '/community/edit', element: <CommunityEdit /> },
      { path: '/report', element: <Report /> },
      { path: '/report/detail', element: <ReportDetail /> },
      { path: '/comment', element: <Comment /> },
      { path: '/comment/detail', element: <CommentDetail /> },
      { path: '/translation', element: <Translation /> },
      { path: '/footer', element: <FooterIcon /> },
      { path: '/footer/edit', element: <FooterIconEdit /> },
      { path: '/referral-stats', element: <ReferralStats /> },
      { path: '/referral-partners', element: <ReferralPartners /> },
      { path: '/referral-partners/edit', element: <ReferralPartnerEdit /> },
      {
        path: '/consultation-requests',
        element: (
          <SuperAdminRoute>
            <ConsultationRequests />
          </SuperAdminRoute>
        ),
      },
      {
        path: '/consultation-requests/edit',
        element: (
          <SuperAdminRoute>
            <ConsultationRequestEdit />
          </SuperAdminRoute>
        ),
      },
    ],
  },
  {
    path: '/login',
    element: <Signin />,
    blockUser: true,
  },
  {
    path: '/oauth',
    element: <OAuth />,
    blockUser: true,
  },
  {
    path: '*',
    element: <Error404 />,
  },
]

export const router = createBrowserRouter(
  routes.map(({ protect, blockUser, ...route }) => {
    if (protect) {
      route.element = <Protected>{route.element}</Protected>
    }

    if (blockUser) {
      route.element = <BlockUser>{route.element}</BlockUser>
    }

    return route
  }),
)

type RouteInfoStore = {
  title: string
  setTitle: (title: string) => void
}

export const useRouteInfo = create<RouteInfoStore>((set) => ({
  title: '',
  setTitle(title) {
    set({ title })
  },
}))
